/**
 * @file Frontend-Client für die Chat-API.
 * Nutzt die zentrale `request`-Funktion und implementiert eine
 * Long-Polling-Strategie, um Nachrichten quasi in Echtzeit zu empfangen.
 */

import { request } from './api.js' // passe Pfad an, falls nötig

/**
 * Lädt Nachrichten für einen Chat, die neuer als eine bestimmte ID sind.
 * @param {string} chatId - Die ID des Chats.
 * @param {number} [last=0] - Die ID, nach der Nachrichten geladen werden sollen.
 * @returns {Promise<Array>} Ein Array von Nachrichtenobjekten.
 */
export async function fetchMessages(chatId, last = 0, signal) {
  if (!chatId) return []
  const data = await request(`/chats/${encodeURIComponent(chatId)}/${last}`, {
    method: 'GET',
    signal // Das Signal wird an den fetch-Aufruf weitergegeben
  })
  const messages = data.messages || data.answer?.messages
  return Array.isArray(messages) ? messages : [];
}

/**
 * Sendet eine neue Nachricht in einen Chat.
 * @param {string} chatId - Die ID des Chats.
 * @param {string} text - Der Nachrichtentext.
 * @param {Array<string>} [attachments=[]] - Eine Liste von Datei-Referenzen.
 * @returns {Promise<object|null>} Die vom Server erstellte und zurückgegebene Nachricht.
 */
export async function sendMessage(chatId, text, attachments = []) {
  if (!chatId) throw new Error('Missing chatId')
  if (!text || !text.trim()) throw new Error('Empty message')
  const body = { text: text.trim(), attachments }
  const data = await request(`/chats/${encodeURIComponent(chatId)}/send`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return data?.message ?? null
}

const GUEST_IP_KEY = 'chat_guest_ip_names_v1'

/**
 * Holt den Zustand für die Benennung von Gästen aus dem localStorage.
 */
function getGuestState() {
  try {
    return JSON.parse(localStorage.getItem(GUEST_IP_KEY) || '{"next":1,"byIp":{}}')
  } catch {
    return { next: 1, byIp: {} }
  }
}

/**
 * Speichert den Zustand für die Benennung von Gästen im localStorage.
 */
function setGuestState(state) {
  try {
    localStorage.setItem(GUEST_IP_KEY, JSON.stringify(state))
  } catch (e){
    console.log(e);
  }
}

/**
 * Weist anonymen Gästen basierend auf ihrer IP-Adresse einen konsistenten Namen zu.
 * Dies geschieht nur clientseitig zur besseren Lesbarkeit im Chat.
 * @param {object} msg - Das Nachrichtenobjekt.
 * @returns {object} Das potenziell modifizierte Nachrichtenobjekt.
 */
function labelGuestByIp(msg) {
  if (!msg?.ip || msg.sender !== null) return msg
  const authorName = msg.author?.nickname
  if (authorName && authorName !== 'Anonymer Gast') return msg
  const state = getGuestState()
  let name = state.byIp[msg.ip]
  if (!name) {
    name = `Wetter-Wetter Gast ${state.next++}`
    state.byIp[msg.ip] = name
    setGuestState(state)
  }
  return {...msg, author: {...(msg.author || {}), nickname: name}}
}

/**
 * Erstellt einen asynchronen Generator, der kontinuierlich neue Nachrichten für einen Chat abruft.
 * Implementiert eine Long-Polling-Strategie.
 * @param {string} id - Die ID des Chats.
 * @param {AbortSignal} signal - Ein Signal, um den Generator von außen zu beenden.
 * @returns {AsyncGenerator<object, void, void>} Ein Generator, der Nachrichtenobjekte liefert.
 */
export async function* subscribeToMessages(id, signal) {
  if (!id) return

  let lastId = 0
  const seen = new Set()

  // Helferfunktion für einen abbrechbaren Timeout.
  const cancellableSleep = (ms) => {
    return new Promise((resolve) => {
      const onAbort = () => {
        clearTimeout(timeout)
        resolve()
      }
      const timeout = setTimeout(() => {
        signal?.removeEventListener('abort', onAbort)
        resolve()
      }, ms)
      signal?.addEventListener('abort', onAbort, { once: true })
    })
  }

  try {
    // Die Schleife läuft nur, solange kein Abbruchsignal gesendet wurde.
    while (!signal?.aborted) {
      const messages = await fetchMessages(id, lastId, signal)
      for (const m of messages) {
        const msgId = Number(m.id)
        if (!Number.isFinite(msgId) || !Number.isInteger(msgId)) continue
        if (msgId > lastId) lastId = msgId
        if (seen.has(msgId)) continue
        seen.add(msgId)
        yield labelGuestByIp(m)
      }
      await cancellableSleep(100)
    }
  } catch (err) {
    // Wenn der Fehler ein AbortError ist, ist das erwartet und kein echter Fehler.
    if (err.name !== 'AbortError') {
      console.error('chat laden error', id, err)
    }
  }
}
